import React, { useEffect, useState } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { DaycareReviewModal } from './DaycareReviewModal';
import { GroomingReviewModal } from './GroomingReviewModal';
import { TrainerReviewModal } from './TrainerReviewModal';
import { OrderReviewModal } from './OrderReviewModal';
import { ReviewModal } from './ReviewModal';
import { BreederReviewModal } from './BreederReviewModal';
import { useTranslation } from 'react-i18next';

export const GlobalReviewTrigger: React.FC = () => {
    const { user } = useAuth();
    const { notifications, markAsRead } = useNotifications();
    const [showDaycareModal, setShowDaycareModal] = useState(false);
    const [showGroomingModal, setShowGroomingModal] = useState(false);
    const [showTrainerModal, setShowTrainerModal] = useState(false);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [showDoctorModal, setShowDoctorModal] = useState(false);
    const [showBreederModal, setShowBreederModal] = useState(false);
    const [doctorAppointment, setDoctorAppointment] = useState<any>(null);
    const [bookingId, setBookingId] = useState<string | null>(null);
    const [appointmentId, setAppointmentId] = useState<string | null>(null);
    const [trainerBookingId, setTrainerBookingId] = useState<string | null>(null);
    const [orderId, setOrderId] = useState<string | null>(null);
    const [orderData, setOrderData] = useState<any>(null);
    const [breederId, setBreederId] = useState<string | null>(null);
    const [breedingEventId, setBreederEventId] = useState<string | null>(null);
    const [providerName, setProviderName] = useState<string>('');
    const { t } = useTranslation();

    useEffect(() => {
        if (!user || user.role !== 'OWNER' || showDaycareModal || showGroomingModal || showTrainerModal || showOrderModal || showBreederModal) return;

        const checkReviews = async () => {
            // 1. Check for Doctor/Vet Review (Previously in Dashboard)
            try {
                // Use the configured api instead of raw axios
                const { api } = await import('../lib/axios');
                const aptRes = await api.get('/appointments');
                const appointmentsData = aptRes.data;
                const unreviewed = appointmentsData.find((apt: any) => 
                    apt.status === 'COMPLETED' && 
                    apt.doctorId && 
                    !apt.review
                );

                if (unreviewed) {
                    const dismissed = JSON.parse(localStorage.getItem('dismissedDoctorReviews') || '[]');
                    if (!dismissed.includes(unreviewed.id)) {
                        // Delay showing the review to allow Ads or initial loading to settle
                        setTimeout(() => {
                           setDoctorAppointment(unreviewed);
                           setShowDoctorModal(true);
                        }, 3000);
                        return;
                    }
                }
            } catch (err) {
                console.error('Failed to fetch appointments for global review trigger', err);
            }

            // 2. Check for Daycare Review Notifications
            const daycareNotif = notifications.find(
                (n) => n.kind === 'DAYCARE_REVIEW' && !n.read
            );

            if (daycareNotif) {
                const match = daycareNotif.message?.match(/bookingId:([\w-]+)/);
                const extractedBookingId = match?.[1];

                if (extractedBookingId) {
                    const dismissed = JSON.parse(localStorage.getItem('dismissedDaycareReviews') || '[]');
                    if (!dismissed.includes(extractedBookingId)) {
                        const nameMatch = daycareNotif.message?.match(/stay at (.*)\?/);
                        setProviderName(nameMatch?.[1] || t('reviews.theDaycare', 'the Daycare'));
                        setBookingId(extractedBookingId);
                        setShowDaycareModal(true);
                        markAsRead(daycareNotif.id);
                        return;
                    }
                }
            }

            // 3. Check for Grooming Completion Notifications
            const groomingNotif = notifications.find(
                (n) => n.kind === 'GROOMING' && n.title?.includes('Completed') && !n.read && n.appointmentId
            );

            if (groomingNotif && groomingNotif.appointmentId) {
                const dismissed = JSON.parse(localStorage.getItem('dismissedGroomingReviews') || '[]');
                if (!dismissed.includes(groomingNotif.appointmentId)) {
                    const nameMatch = groomingNotif.message?.match(/at (.*) has been completed/);
                    setProviderName(nameMatch?.[1] || t('reviews.theGroomer', 'the Groomer'));
                    setAppointmentId(groomingNotif.appointmentId);
                    setShowGroomingModal(true);
                    markAsRead(groomingNotif.id);
                    return;
                }
            }

            // 4. Check for Training Completion Notifications
            const trainingNotif = notifications.find(
                (n) => n.kind === 'TRAINING' && n.title === 'Session Completed' && !n.read && n.trainingBookingId
            );

            if (trainingNotif && trainingNotif.trainingBookingId) {
                const tBookingId = trainingNotif.trainingBookingId;
                const dismissed = JSON.parse(localStorage.getItem('dismissedTrainerReviews') || '[]');
                if (!dismissed.includes(tBookingId)) {
                    setTrainerBookingId(tBookingId);
                    setShowTrainerModal(true);
                    markAsRead(trainingNotif.id);
                    return;
                }
            }

            // 5. Check for Order Delivery / Prescription Dispensed Notifications
            const orderNotif = notifications.find(
                (n) => n.kind === 'ORDER' && 
                (n.title === 'Order Delivered!' || n.title === 'Prescription Dispensed' || n.title?.includes('Dispensed') || n.title?.includes('Delivered')) && 
                !n.read && n.orderId
            );

            if (orderNotif && orderNotif.orderId) {
                const dismissed = JSON.parse(localStorage.getItem('dismissedOrderReviews') || '[]');
                if (!dismissed.includes(orderNotif.orderId)) {
                    setOrderId(orderNotif.orderId);
                    setShowOrderModal(true);
                    markAsRead(orderNotif.id);
                    return;
                }
            }

            // Direct API check for any unreviewed delivered orders / dispensed prescriptions
            try {
                const { api } = await import('../lib/axios');
                const pendingOrderRes = await api.get('/ecommerce/pending-review');
                if (pendingOrderRes.data?.hasPending && pendingOrderRes.data.order?.id) {
                    const unreviewed = pendingOrderRes.data.order;
                    const dismissed = JSON.parse(localStorage.getItem('dismissedOrderReviews') || '[]');
                    if (!dismissed.includes(unreviewed.id)) {
                        setOrderId(unreviewed.id);
                        setOrderData(unreviewed);
                        setShowOrderModal(true);
                        return;
                    }
                }
            } catch (err) {
                // continue
            }

            // 6. Check for Breeder Review (Direct API Check or Notification)
            try {
                const { api } = await import('../lib/axios');
                const pendingRes = await api.get('/breeder/pending-review');
                if (pendingRes.data?.hasPending && pendingRes.data.breederId) {
                    const p = pendingRes.data;
                    const dismissed = JSON.parse(localStorage.getItem('dismissedBreederReviews') || '[]');
                    if (!dismissed.includes(p.breederId)) {
                        setProviderName(p.breederName || t('reviews.theBreeder', 'the Breeder'));
                        setBreederId(p.breederId);
                        setBreederEventId(p.breedingEventId || null);
                        setShowBreederModal(true);
                        return;
                    }
                }
            } catch (err) {
                // Fallback to checking notifications
            }

            const breederNotif = notifications.find(
                (n) => (n.kind === 'BREEDER_REVIEW' || (n.kind === 'BREEDER' && n.title?.includes('Completed'))) && !n.read
            );

            if (breederNotif) {
                const match = breederNotif.message?.match(/breederId:([\w-]+)/);
                const eventMatch = breederNotif.message?.match(/eventId:([\w-]+)/);
                const extractedBreederId = match?.[1];

                if (extractedBreederId) {
                    const dismissed = JSON.parse(localStorage.getItem('dismissedBreederReviews') || '[]');
                    if (!dismissed.includes(extractedBreederId)) {
                        const nameMatch = breederNotif.message?.match(/with (.*) is complete/) || 
                                          breederNotif.message?.match(/with (.*) has been/) ||
                                          breederNotif.message?.match(/with (.*)!/);
                        setProviderName(nameMatch?.[1] || t('reviews.theBreeder', 'the Breeder'));
                        setBreederId(extractedBreederId);
                        setBreederEventId(eventMatch?.[1] || null);
                        setShowBreederModal(true);
                        return;
                    }
                }
            }
        };

        checkReviews();
    }, [notifications, user, showDaycareModal, showGroomingModal, showTrainerModal, showOrderModal, showBreederModal, markAsRead]);

    const handleCloseBreeder = () => {
        if (breederId) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedBreederReviews') || '[]');
            if (!dismissed.includes(breederId)) {
                dismissed.push(breederId);
                localStorage.setItem('dismissedBreederReviews', JSON.stringify(dismissed));
            }
        }
        setShowBreederModal(false);
        setBreederId(null);
        setBreederEventId(null);
    };

    const handleCloseDaycare = () => {
        if (bookingId) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedDaycareReviews') || '[]');
            if (!dismissed.includes(bookingId)) {
                dismissed.push(bookingId);
                localStorage.setItem('dismissedDaycareReviews', JSON.stringify(dismissed));
            }
        }
        setShowDaycareModal(false);
        setBookingId(null);
    };

    const handleCloseGrooming = () => {
        if (appointmentId) {
            // Check which list to add to (Grooming or Doctor)
            // For safety, add to both or we can be more specific
            const dismissedG = JSON.parse(localStorage.getItem('dismissedGroomingReviews') || '[]');
            const dismissedD = JSON.parse(localStorage.getItem('dismissedDoctorReviews') || '[]');
            
            if (!dismissedG.includes(appointmentId)) dismissedG.push(appointmentId);
            if (!dismissedD.includes(appointmentId)) dismissedD.push(appointmentId);
            
            localStorage.setItem('dismissedGroomingReviews', JSON.stringify(dismissedG));
            localStorage.setItem('dismissedDoctorReviews', JSON.stringify(dismissedD));
        }
        setShowGroomingModal(false);
        setAppointmentId(null);
    };

    const handleCloseDoctor = () => {
        if (doctorAppointment) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedDoctorReviews') || '[]');
            if (!dismissed.includes(doctorAppointment.id)) {
                dismissed.push(doctorAppointment.id);
                localStorage.setItem('dismissedDoctorReviews', JSON.stringify(dismissed));
            }
        }
        setShowDoctorModal(false);
        setDoctorAppointment(null);
    };

    const handleCloseTrainer = () => {
        if (trainerBookingId) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedTrainerReviews') || '[]');
            if (!dismissed.includes(trainerBookingId)) {
                dismissed.push(trainerBookingId);
                localStorage.setItem('dismissedTrainerReviews', JSON.stringify(dismissed));
            }
        }
        setShowTrainerModal(false);
        setTrainerBookingId(null);
    };

    const handleCloseOrder = () => {
        if (orderId) {
            const dismissed = JSON.parse(localStorage.getItem('dismissedOrderReviews') || '[]');
            if (!dismissed.includes(orderId)) {
                dismissed.push(orderId);
                localStorage.setItem('dismissedOrderReviews', JSON.stringify(dismissed));
            }
        }
        setShowOrderModal(false);
        setOrderId(null);
        setOrderData(null);
    };

    return (
        <>
            {showDoctorModal && doctorAppointment && (
                <ReviewModal
                    isOpen={showDoctorModal}
                    appointment={doctorAppointment}
                    onClose={(completed?: boolean) => {
                        handleCloseDoctor();
                        if (completed) {
                            // If successfully submitted, mark as reviewed locally or force a refetch if needed
                        }
                    }}
                />
            )}
            {showOrderModal && orderId && (
                <OrderReviewModal
                    isOpen={showOrderModal}
                    orderId={orderId}
                    orderData={orderData}
                    onClose={handleCloseOrder}
                    onSuccess={() => {
                        setShowOrderModal(false);
                        setOrderId(null);
                        setOrderData(null);
                    }}
                />
            )}
            {showDaycareModal && bookingId && (
                <DaycareReviewModal
                    isOpen={showDaycareModal}
                    onClose={handleCloseDaycare}
                    dayCareBookingId={bookingId}
                    daycareName={providerName}
                    onSuccess={() => {
                        setShowDaycareModal(false);
                        setBookingId(null);
                    }}
                />
            )}

            {showGroomingModal && appointmentId && (
                <GroomingReviewModal
                    isOpen={showGroomingModal}
                    onClose={handleCloseGrooming}
                    appointmentId={appointmentId}
                    agencyName={providerName}
                    onSuccess={() => {
                        setShowGroomingModal(false);
                        setAppointmentId(null);
                    }}
                />
            )}

            {showTrainerModal && trainerBookingId && (
                <TrainerReviewModal
                    isOpen={showTrainerModal}
                    trainingBookingId={trainerBookingId}
                    trainerName={providerName || undefined}
                    onClose={handleCloseTrainer}
                    onSuccess={() => {
                        setShowTrainerModal(false);
                        setTrainerBookingId(null);
                    }}
                />
            )}

            {showBreederModal && breederId && (
                <BreederReviewModal
                    isOpen={showBreederModal}
                    breederId={breederId}
                    breedingEventId={breedingEventId}
                    breederName={providerName}
                    onClose={handleCloseBreeder}
                    onSuccess={() => {
                        const notif = notifications.find(n => n.kind === 'BREEDER_REVIEW' && !n.read);
                        if (notif) markAsRead(notif.id);
                        setShowBreederModal(false);
                        setBreederId(null);
                        setBreederEventId(null);
                    }}
                />
            )}
        </>
    );
};
